import {ChangeDetectorRef, Component} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {ToastrService} from 'ngx-toastr';
import {NgSelectModule} from "@ng-select/ng-select";
import {NgxDatatableModule} from "@swimlane/ngx-datatable";
import {SpinnerComponent} from "@shared/components/spinner/spinner.component";
import {PillTabsComponent, PillTab} from "@shared/components/pill-tabs/pill-tabs.component";
import {
    WeekCardSelectorComponent,
    WeekAdherenceEntry,
    VisibleWeekRange
} from "@shared/components/week-card-selector/week-card-selector.component";
import {PatientStatisticsService} from "@shared/api/services/v2/patient-statistics.service";
import {ApiService} from "@ctms/api.service";
import {NgApexchartsModule} from "ng-apexcharts";
import {BreadcrumbService} from "@cdss/services/breadcrumb.service";
import {ActivatedRoute, NavigationBehaviorOptions, Router} from "@angular/router";
import {PatientsService} from '@shared/api/services/v2/patients.service';
import {AuthService} from "@shared/services/security/auth.service";
import {
    PatientOverviewV2WeeklySummaryComponent
} from "@cdss/pages/patient-overview-v2/weekly-summary/patient-overview-v2-weekly-summary.component";
import {LogginedUser} from "@models/user.model";
import {Patient} from "@shared/api/models/query/patient.model";
import {UserCareProgramsService} from "@shared/api/services/v2/user-care-programs.service";
import {Pagination, PaginationResponse} from "@models/pagination.model";
import {UserCareProgram, UserCareProgramWeek} from "@shared/api/models/query/user-care-program.model";
import {
    PatientOverviewV2TimelineComponent
} from "@cdss/pages/patient-overview-v2/timeline/patient-overview-v2-timeline.component";
import {
    PatientOverviewV2AdherenceComponent
} from "@cdss/pages/patient-overview-v2/adherence/patient-overview-v2-adherence.component";
import {
    PatientOverviewV2FooterComponent
} from "@cdss/pages/patient-overview-v2/footer/patient-overview-v2-footer.component";
import {PatientDetailHeaderComponent} from "@cdss/pages/patient-overview-v2/components/patient-detail-header/patient-detail-header.component";
import {PatientRecordsComponent} from "@cdss/pages/patient-overview/records/patient-records.component";
import dayjs from "dayjs";

@Component({
    selector: 'cdss-patient-overview-v2-page',
    imports: [CommonModule, ReactiveFormsModule, SpinnerComponent, NgSelectModule, NgxDatatableModule, NgApexchartsModule, PatientOverviewV2WeeklySummaryComponent, FormsModule, PatientOverviewV2TimelineComponent, PatientOverviewV2AdherenceComponent, PatientOverviewV2FooterComponent, PatientRecordsComponent, PatientDetailHeaderComponent, PillTabsComponent, WeekCardSelectorComponent],
    providers: [ApiService],
    templateUrl: './patient-overview-v2.page.html',
    styleUrl: './patient-overview-v2.page.scss'
})
export class PatientOverviewV2Page {
    loading = true
    activeTab: number = 1

    records: boolean = false

    selectedWeekNumber: number = 1
    selectedWeek: UserCareProgramWeek | null = null

    /** Optional ?dateStart= query param (e.g. arriving via "Back" from a Daily/Weekly detail
     *  page) naming a date to open the matching week for, instead of defaulting to the
     *  care programme's current week (UNIVAPLAT-2360 follow-up). Consumed once on init —
     *  after that, {@see syncQueryParams} is the URL's only writer. */
    private requestedDateStart: string | null = null

    id: number
    patient: Patient
    currentUser: LogginedUser
    userCareProgram: UserCareProgram

    // Keyed by `${weekNumber}-${type}` since a week's adherence differs by
    // Patient/Carer tab — a plain week-number key would be ambiguous.
    adherenceByWeek: Record<string, WeekAdherenceEntry> = {};
    loadingWeekNumbers: number[] = [];
    private lastVisibleRange: VisibleWeekRange | null = null;

    get tabs(): PillTab[] {
        const result: PillTab[] = [];
        if (this.patient?.user?.phoneNumber != null) {
            result.push({id: 1, label: 'Patient'});
        }
        result.push({id: 2, label: 'Carer'});
        return result;
    }

    constructor(
        public toastr: ToastrService,
        private breadcrumbService: BreadcrumbService,
        private activeRoute: ActivatedRoute,
        private router: Router,
        private patientsService: PatientsService,
        private userCareProgramsService: UserCareProgramsService,
        private authService: AuthService,
        private cdr: ChangeDetectorRef,
        private patientStatisticsService: PatientStatisticsService
    ) {
    }

    ngOnInit() {
        this.id = this.activeRoute.snapshot.params['id']
        this.requestedDateStart = this.activeRoute.snapshot.queryParams['dateStart'] ?? null
        const requestedType = this.activeRoute.snapshot.queryParams['type'] ?? null
        if (requestedType === 'carer') {
            this.activeTab = 2
        } else if (requestedType === 'patient') {
            this.activeTab = 1
        }
        this.authService.getLogginedUser().subscribe((user: LogginedUser) => {
            this.currentUser = user
            this.breadcrumbService.set([
                {title: "Overview", url: null},
            ])

            this.loading = false
        });
        this.getPatient()
    }

    async getPatient() {
        this.patient = await this.patientsService.getById(this.id)
        this.applyTabVisibility()
        this.getUserCareProgram()
    }

    private applyTabVisibility() {
        // Hard constraint: a patient with no phone number has no patient-side account, so the
        // Patient tab has nothing to show — this always wins over a requested ?type=patient.
        if (this.patient.user.phoneNumber == null) {
            this.activeTab = 2
        }
    }

    getUserCareProgram() {
        this.loading = true
        const pagination = new Pagination()
        pagination.filter = {
            user: this.patient.user.phoneNumber == null? this.patient.carer.user.id : this.patient.user.id
        }
        this.userCareProgramsService.listPaginated(pagination).subscribe({
            next: (response: PaginationResponse<UserCareProgram>) => {
                if (!response?.data?.length) {
                    this.loading = false
                    return
                }
                this.userCareProgram = response.data[0]
                this.selectedWeekNumber = this.resolveWeekNumberForDate(this.requestedDateStart)
                    ?? this.userCareProgram.currentWeekNumber
                this.selectedWeek = this.getWeek(this.selectedWeekNumber)
                this.syncQueryParams()
                this.cdr.detectChanges()
                this.loading = false
            },
            error: (error) => {
                this.loading = false
                this.toastr.error(error.error.message)
            }
        })
    }

    onWeekChange(weekNumber: number) {
        this.selectedWeekNumber = weekNumber
        this.selectedWeek = this.getWeek(this.selectedWeekNumber)
        this.syncQueryParams()
    }

    onActiveTabChange(tabId: number): void {
        this.activeTab = tabId;
        this.syncQueryParams();
        // The active tab drives which type's adherence numbers the week
        // cards should show; re-run the last known visible range so the
        // newly active type's data gets fetched (composite cache key means
        // the other type's cache entries are left untouched).
        if (this.lastVisibleRange) {
            this.onVisibleRangeChanged(this.lastVisibleRange);
        }
    }

    get currentAdherenceType(): 'patient' | 'carer' {
        return this.activeTab === 2 ? 'carer' : 'patient';
    }

    get visibleAdherenceByWeek(): Record<number, WeekAdherenceEntry> {
        const type = this.currentAdherenceType;
        const suffix = `-${type}`;
        const result: Record<number, WeekAdherenceEntry> = {};
        for (const key of Object.keys(this.adherenceByWeek)) {
            if (key.endsWith(suffix)) {
                const weekNumber = Number(key.slice(0, key.length - suffix.length));
                result[weekNumber] = this.adherenceByWeek[key];
            }
        }
        return result;
    }

    /** ARFID signal for the carer weekly panel's meal plan card copy (UNIVAPLAT-3041,
     *  `resolveWeeklyCarerCopy`). Sourced from `userCareProgram.careProgram.scope`, already
     *  fetched by {@see getUserCareProgram} via `GET /api/v2/user-care-programs` — no
     *  additional request needed. */
    get isArfid(): boolean {
        return this.userCareProgram?.careProgram?.scope === 'ARFID'
    }

    /**
     * Keeps ?type= and ?dateStart= in the URL matching the active tab / selected week, so the
     * URL is always a complete, shareable/bookmarkable description of what the page shows
     * (UNIVAPLAT-2360 follow-up). Landing here with no query params resolves to (patient,
     * current week) and this then writes that back into the URL; every subsequent week or tab
     * change updates it the same way. Uses `replaceUrl` so browsing weeks/tabs never spams
     * browser history — only the initial navigation to this page is a "real" history entry.
     */
    private syncQueryParams(): void {
        if (!this.selectedWeek) return
        const type = this.activeTab === 2 ? 'carer' : 'patient'
        this.router.navigate([], {
            relativeTo: this.activeRoute,
            queryParams: {type, dateStart: this.selectedWeek.dateStart},
            replaceUrl: true,
        })
    }

    /** Resolves the care-programme week whose [dateStart, dateEnd] range contains `date`, or
     *  null when there is no such date (no request, or no matching week). Matches by date
     *  range rather than exact `dateStart` equality so it tolerates the caller's date not
     *  landing exactly on a week boundary. */
    private resolveWeekNumberForDate(date: string | null): number | null {
        if (!date) return null
        const target = dayjs(date)
        const week = this.userCareProgram?.config?.weeks?.find(week =>
            !target.isBefore(dayjs(week.dateStart), 'day') && !target.isAfter(dayjs(week.dateEnd), 'day')
        )
        return week?.number ?? null
    }

    getWeek(weekNumber: number): null | UserCareProgramWeek {
        let week = this.userCareProgram?.config?.weeks.find(week => week.number === weekNumber)
        if (!week) return null
        week.patientId = this.patient.id
        return week
    }

    goBackToDashboard(): void {
        this.router.navigate(['/cdss/dashboard'])
    }

    showRecords() {
        this.records = true
    }
    hideRecords() {
        this.records = false
    }

    navigateToPatientWeeklyDetail(): void {
        if (!this.selectedWeek) return;
        this.router.navigate(
            ['/cdss/patient-overview', this.id, 'patient-weekly'],
            {queryParams: {dateStart: this.selectedWeek.dateStart, weekNumber: this.selectedWeekNumber, careProgramId: this.userCareProgram?.id ?? null}}
        );
    }

    navigateToCarerWeeklyDetail(): void {
        if (!this.selectedWeek) return;
        this.router.navigate(
            ['/cdss/patient-overview', this.id, 'carer-weekly'],
            {queryParams: {dateStart: this.selectedWeek.dateStart, weekNumber: this.selectedWeekNumber, careProgramId: this.userCareProgram?.id ?? null}}
        );
    }

    async onVisibleRangeChanged(range: VisibleWeekRange): Promise<void> {
        this.lastVisibleRange = range;
        const type = this.currentAdherenceType;
        const weekNumbersToFetch: number[] = [];
        for (let n = range.from; n <= range.to; n++) {
            if (this.adherenceByWeek[`${n}-${type}`] === undefined && !this.loadingWeekNumbers.includes(n)) {
                weekNumbersToFetch.push(n);
            }
        }
        if (!weekNumbersToFetch.length) return;

        this.loadingWeekNumbers = [...this.loadingWeekNumbers, ...weekNumbersToFetch];

        await Promise.all(weekNumbersToFetch.map(async (weekNumber) => {
            const week = this.getWeek(weekNumber);
            if (!week) {
                this.loadingWeekNumbers = this.loadingWeekNumbers.filter(n => n !== weekNumber);
                return;
            }
            try {
                const summary = await this.patientStatisticsService.getWeeklySummary(
                    week.patientId, week.dateStart, week.dateEnd, type
                );
                const chapterAdherence = summary?.adherence?.chapters?.scheduled
                    ? summary.adherence.chapters.completed * 100 / summary.adherence.chapters.scheduled
                    : null;
                const checkinAdherence = summary?.adherence?.checkins?.scheduled
                    ? summary.adherence.checkins.completed * 100 / summary.adherence.checkins.scheduled
                    : null;
                this.adherenceByWeek = {
                    ...this.adherenceByWeek,
                    [`${weekNumber}-${type}`]: {chapterAdherence, checkinAdherence}
                };
            } catch {
                // Do not cache a failure — an empty/failed entry is
                // indistinguishable from a legitimate "nothing scheduled"
                // result and would never be retried. Leaving the key
                // undefined means it's naturally retried next time this
                // week falls in a visible range.
            } finally {
                this.loadingWeekNumbers = this.loadingWeekNumbers.filter(n => n !== weekNumber);
            }
        }));
    }
}
